/**
 * RISE — bubbles, balloons, lanterns, embers.
 *
 * FALL inverted is not RISE, and shipping it as `baseSpeed: -46` is the version
 * that looks wrong. Three differences are load-bearing:
 *
 *   1. BIG RISES FASTER, AND THE RELATIONSHIP IS STEEPER. Buoyancy scales with
 *      volume while drag scales with area, so a large bubble climbs much faster
 *      than a small one — a far stronger separation than terminal velocity gives
 *      falling snow. Reusing FALL's mild size/speed coupling makes a bubble
 *      field look like snow running backwards, which is exactly what it is.
 *   2. THE WOBBLE IS A LATERAL DRIFT, NOT A SWAY. A rising bubble follows a
 *      slow, wandering path rather than oscillating about a fixed column, so the
 *      horizontal term integrates rather than being assigned.
 *   3. THEY FADE AT THE TOP. A bubble that hits y=0 and vanishes reads as a
 *      clipping bug. Lanterns especially need to recede.
 */

import { clamp } from '../budget.ts';
import type { Family, Particle, StepContext } from '../types.ts';

export interface RiseOptions {
  depth: 1 | 2 | 3;
  baseSize: number;
  /** Climb speed in CSS px/s at size scale 1. */
  baseSpeed: number;
  /** How strongly size drives speed. 1 = linear, 2 = quadratic. */
  buoyancy: number;
  /** Lateral drift amplitude, CSS px/s. */
  wobble: number;
  /** Drift cycles per second. Kept far below the WCAG flash bar. */
  wobbleHz: number;
  /** Gentle rotation, radians/s. Balloons barely turn; embers tumble. */
  spin: number;
  /** Fraction of the viewport height over which the particle fades out at the top. */
  fadeTop: number;
  cellCount: number;
  tintCount: number;
  sizeScale: number;
}

/** [sizeScale, speedScale, alpha] per depth band. */
const BANDS: ReadonlyArray<readonly [number, number, number]> = [
  [0.5, 0.6, 0.4],
  [0.78, 0.85, 0.72],
  [1.2, 1.25, 1.0],
];

export function createRiseFamily(opts: RiseOptions): Family {
  const bands = BANDS.slice(0, opts.depth);

  return {
    id: 'rise',

    spawn(p: Particle, ctx: StepContext, rnd: () => number) {
      const band = (rnd() * bands.length) | 0;
      const [sizeScale, speedScale, alpha] = bands[band];

      p.depth = band;
      p.alpha = alpha;
      p.cell = (rnd() * opts.cellCount) | 0;
      p.tint = (rnd() * opts.tintCount) | 0;

      // A wide size spread, because the buoyancy curve below turns it into a
      // wide SPEED spread, which is what makes the field read as depth.
      const jitter = 0.55 + rnd() * 0.9;
      p.size = opts.baseSize * sizeScale * jitter * opts.sizeScale;

      p.x = rnd() * ctx.w;
      // Spread the initial field instead of launching a floor-load at once.
      p.y = ctx.h + rnd() * ctx.h * 0.9;

      // Buoyancy: speed rises with size, steeply. `jitter ** buoyancy` at
      // buoyancy = 2 makes a 1.45x bubble climb 2.1x faster.
      p.vy = -opts.baseSpeed * speedScale * Math.pow(jitter, opts.buoyancy);
      p.vx = 0;

      p.r = rnd() * Math.PI * 2;
      p.vr = (rnd() - 0.5) * 2 * opts.spin;
      p.age = 0;
      p.phase = rnd() * Math.PI * 2;
    },

    step(p: Particle, ctx: StepContext): boolean {
      p.age += ctx.dt;
      const bandSpeed = BANDS[p.depth][1];

      // The drift INTEGRATES — the particle wanders rather than oscillating
      // about a fixed column. Two frequencies beat against each other so the
      // path never repeats visibly.
      const drift =
        opts.wobble *
        bandSpeed *
        (Math.sin(p.phase + ctx.t * opts.wobbleHz * Math.PI * 2) +
          0.4 * Math.sin(p.phase * 1.7 + ctx.t * opts.wobbleHz * Math.PI * 3.1));

      p.vx = drift + ctx.wind * 70 * bandSpeed;
      p.x += p.vx * ctx.dt;
      p.y += p.vy * ctx.speed * ctx.dt;
      p.r += p.vr * ctx.dt;

      // Fade into the top rather than clipping against it.
      const fadeBand = ctx.h * opts.fadeTop;
      const base = BANDS[p.depth][2];
      p.alpha = p.y < fadeBand ? base * clamp(p.y / fadeBand, 0, 1) : base;

      const margin = p.size;
      if (p.x < -margin) p.x = ctx.w + margin;
      else if (p.x > ctx.w + margin) p.x = -margin;

      // Recycle once fully above the fold.
      return p.y >= -p.size * 2;
    },
  };
}

export const RISE_PRESETS = {
  bubbles: {
    baseSize: 18,
    baseSpeed: 62,
    buoyancy: 2,
    wobble: 16,
    wobbleHz: 0.32,
    spin: 0.3,
    fadeTop: 0.14,
  },
  balloons: {
    baseSize: 40,
    baseSpeed: 44,
    buoyancy: 1.4,
    wobble: 10,
    wobbleHz: 0.14,
    spin: 0.25,
    fadeTop: 0.2,
  },
  lanterns: {
    baseSize: 30,
    baseSpeed: 26,
    buoyancy: 1.2,
    wobble: 7,
    wobbleHz: 0.1,
    spin: 0.12,
    fadeTop: 0.3,
  },
  embers: {
    baseSize: 8,
    baseSpeed: 78,
    buoyancy: 1.8,
    wobble: 26,
    wobbleHz: 0.5,
    spin: 1.8,
    fadeTop: 0.35,
  },
  /** Butterflies drift upward slowly and wander widely. */
  butterflies: {
    baseSize: 22,
    baseSpeed: 30,
    buoyancy: 1.1,
    wobble: 46,
    wobbleHz: 0.42,
    spin: 0.5,
    fadeTop: 0.18,
  },
} as const satisfies Record<
  string,
  Omit<RiseOptions, 'depth' | 'cellCount' | 'tintCount' | 'sizeScale'>
>;

export type RisePresetId = keyof typeof RISE_PRESETS;

export function riseOptions(
  preset: RisePresetId,
  o: Pick<RiseOptions, 'depth' | 'cellCount' | 'tintCount' | 'sizeScale'>
): RiseOptions {
  const base = RISE_PRESETS[preset];
  return { ...base, ...o, wobbleHz: clamp(base.wobbleHz, 0, 1) };
}
